import { useRouter } from "next/router";
import ReactPlayer from "react-player";
import { AspectRatio, Box, Flex, Grid, GridItem, Image, Text, VStack } from "@chakra-ui/react";
import BetaSignupForm from "@components/site/forms/BetaSignupForm";
import { ReactNode, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useWindowSiteStore } from "@store/windowSiteStore";
import { scaledPixel as scaledPixelRoot, getScaleValues } from "@utils/helpers";
import SocialMediaIcons from "../layout/SocialMediaIcons";
import NextLink from "next/link";
import dynamic from "next/dynamic";
import { Footer } from "../layout";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionGridItem = motion(GridItem);
const MotionImage = motion(Image);

const DESKTOP_BREAKPOINT = 600;

type JoinBetaProps = {
    referer?: string;
    username?: string;
    video?: string;
    subtitle?: ReactNode;
    callToAction?: string;
};

export const JoinBeta = ({
    referer,
    username,
    video = "/images/beta/beyond-rarity.mp4",
    subtitle = (
        <>
            Soon, you can know the value of an NFT.&nbsp;
            <strong>Instantly.</strong>
        </>
    ),
    callToAction = "Claim your spot to the beta with your email:",
}: JoinBetaProps) => {
    // console.debug("Join Beta - referer url: ", referer);
    const windowWidth = useWindowSiteStore((state) => state.windowWidth);
    const windowHeight = useWindowSiteStore((state) => state.windowHeight);

    const isDesktop = windowWidth > DESKTOP_BREAKPOINT;

    //const normalScale = Math.max(Math.min(windowWidth / 1024, 1), 0.25);
    const normalScale = 1;
    const largeScale = Math.max(Math.min(windowWidth / 2048, 1), 0.25);

    const scaledPixel = (desktopPixels, mobilePixels = null) => {
        if (
            mobilePixels != null &&
            (windowWidth < DESKTOP_BREAKPOINT || windowHeight < DESKTOP_BREAKPOINT)
        ) {
            return scaledPixelRoot(mobilePixels, normalScale);
        }

        return scaledPixelRoot(desktopPixels, normalScale);
    };

    const verticalScaledPixel = (desktopPixels, mobilePixels = null) => {
        if (
            mobilePixels != null &&
            (windowWidth < DESKTOP_BREAKPOINT || windowHeight < DESKTOP_BREAKPOINT)
        ) {
            return scaledPixelRoot(mobilePixels, largeScale);
        }

        return scaledPixelRoot(desktopPixels, largeScale);
    };

    const slideUpAnimation = (delay = 0) => ({
        initial: {
            translateY: isDesktop ? "100px" : "50px",
            opacity: 0,
        },
        whileInView: {
            translateY: "0px",
            opacity: 1,
        },
        transition: {
            duration: 0.5,
            ease: "easeOut",
            delay: delay,
        },
        viewport: {
            once: true,
        },
    });

    return (
        <>
            <Flex direction="column" overflowX="hidden" bgColor="#282D41" pb={120}>
                <AspectRatio minW="100%" maxH="80vh" ratio={16 / 9} bgColor="#0003">
                    <ReactPlayer
                        url={video}
                        // playIcon={<MdPlayCircleOutline size="20%" />}
                        // light={tokenRating.opensea?.image_url}
                        controls={false}
                        // playing={tokenRating.opensea?.image_url}
                        playing={true}
                        muted={true}
                        loop={true}
                        width="100%"
                        height="auto"
                        playsinline={true}
                    />
                </AspectRatio>
                <VStack
                    pos="relative"
                    w="100%"
                    p={scaledPixel(50, 20)}
                    mt={scaledPixel(20, 10)}
                    fontSize={scaledPixel(28, 16)}
                    fontWeight={300}
                    overflowX="hidden"
                >
                    <Flex
                        fontFamily="'Fira Sans', sans-serif"
                        fontSize={scaledPixel(44, 20)}
                        fontWeight={600}
                        bgClip="text"
                        bgGradient="linear(90deg, #F77A59 0%, #EA3A65 100%) 0% 0% no-repeat"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Box textAlign="center">
                            Welcome to the Beta{username ? `, @${username}` : null}!
                        </Box>
                    </Flex>
                    <Flex w="100%" justifyContent="center" alignItems="center">
                        <Box textAlign="center">{subtitle}</Box>
                    </Flex>
                    <Flex
                        pos="relative"
                        w="100%"
                        direction="column"
                        justifyContent="stretch"
                        alignItems="center"
                        pt={verticalScaledPixel(80, 80)}
                    >
                        <Flex
                            pos="absolute"
                            mt={scaledPixel(-100, -100)}
                            w="100vw"
                            h="100%"
                            justifyContent="center"
                            alignItems="start"
                        >
                            <Image
                                src="/images/beta/divider.png"
                                w="100%"
                                minW={scaledPixel(1024, 600)}
                                maxW={scaledPixel(1024, 600)}
                                h="auto"
                                alt="Beyond Rarity - Divide"
                            />
                        </Flex>
                        <MotionFlex
                            // w="100%"
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                            gap={scaledPixel(15)}
                            {...slideUpAnimation(0.1)}
                        >
                            <Box textAlign="center">{callToAction}</Box>
                            <Flex
                                w={isDesktop ? "100%" : "80vw"}
                                minW={isDesktop ? "550px" : "200px"}
                                maxW="800px"
                            >
                                <BetaSignupForm
                                    scaledPixel={scaledPixel}
                                    emailPlaceholderText="wagmi@email.com"
                                    submitButtonLabel="Claim"
                                    height={isDesktop ? 50 : 30}
                                    buttonPadding={isDesktop ? 50 : 20}
                                    inputFontSize={isDesktop ? 24 : 14}
                                    buttonFontSize={isDesktop ? 26 : 16}
                                    statusFontSize={isDesktop ? 24 : 12}
                                    twitterHandle={username}
                                    referer={referer}
                                />
                            </Flex>
                        </MotionFlex>
                    </Flex>
                    <Flex
                        w="100%"
                        pt={scaledPixel(160, 70)}
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Grid templateColumns="auto auto" gap={scaledPixel(20, 10)}>
                            <GridItem>
                                <MotionImage
                                    src="/images/beta/onboarding-boost.svg"
                                    alt="Beyond Rarity - Boost"
                                    w={scaledPixel(90, 60)}
                                    {...slideUpAnimation(0.1)}
                                />
                            </GridItem>
                            <GridItem display="flex" justifyContent="start" alignItems="center">
                                <MotionBox {...slideUpAnimation(0.1)}>
                                    Buy and sell with confidence
                                </MotionBox>
                            </GridItem>

                            <GridItem>
                                <MotionImage
                                    src="/images/beta/algo-circle-simple.svg"
                                    alt="Beyond Rarity - Algo"
                                    w={scaledPixel(90, 60)}
                                    {...slideUpAnimation(0.1)}
                                />
                            </GridItem>
                            <GridItem display="flex" justifyContent="start" alignItems="center">
                                <MotionBox {...slideUpAnimation(0.1)}>
                                    Revolutionary algorithm
                                </MotionBox>
                            </GridItem>

                            <GridItem>
                                <MotionImage
                                    src="/images/beta/tears-devs.svg"
                                    alt="Beyond Rarity - Dev Tears"
                                    w={scaledPixel(90, 60)}
                                    {...slideUpAnimation(0.1)}
                                />
                            </GridItem>
                            <GridItem display="flex" justifyContent="start" alignItems="center">
                                <MotionBox {...slideUpAnimation(0.1)}>
                                    Made from the tears of devs
                                </MotionBox>
                            </GridItem>
                        </Grid>
                    </Flex>
                </VStack>
            </Flex>
            <Footer />
        </>
    );
};

export const DynamicJoinBeta = dynamic(() => import("./JoinBeta").then((mod) => mod.JoinBeta), {
    ssr: false,
});

export default DynamicJoinBeta;
